import { useState } from "react";
import { srcSetFor } from "@/lib/responsiveImage";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import { SectionLoading, SectionError, SectionEmpty } from "@/components/RichContent";
import { useBlogs } from "@/hooks/usePublicData";
import { formatSGDate } from "@/lib/format";

const ALL = "All";

export default function Blogs() {
  const [category, setCategory] = useState(ALL);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch, isFetching } = useBlogs({
    page,
    pageSize: 12,
    category: category === ALL ? undefined : category,
  });

  const items = (data && data.items) || [];
  // Categories come from the API response so the filter bar can never offer a
  // category the backend does not recognise.
  const categories = [ALL, ...((data && data.categories) || [])];
  const totalPages = (data && data.totalPages) || 1;

  return (
    <>
      <SeoHead pageKey="blogs" />

      <PageHero
        eyebrow="Blogs & Insights"
        chapter="Chapter — Journal"
        lines={["Research notes from", "the advisory team."]}
        blurb="Long-form editorial by the PIRAC Research and Advisory Team. Publication cadence is deliberate — new pieces only when there is something to say."
      />

      <section className="bg-[#060f1e] py-14 border-b border-[#1a2940]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-wrap gap-3">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => { setCategory(c); setPage(1); }}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 h-11 text-sm tracking-wide border transition-all duration-300 ${
                category === c
                  ? "border-[#cca24d] bg-[#cca24d]/10 text-[#cca24d]"
                  : "border-[#1a2940] text-[#d8d8d8] hover:border-[#cca24d]/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#060f1e] py-16 lg:py-24">
        {isLoading ? (
          <SectionLoading label="Loading insights" />
        ) : isError ? (
          <SectionError onRetry={refetch} />
        ) : (
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 transition-opacity duration-200 ${
                isFetching ? "opacity-60" : "opacity-100"
              }`}
            >
              {items.length === 0 ? (
                <SectionEmpty
                  title="Nothing published here yet"
                  description={
                    category === ALL
                      ? "New research notes are published when there is something worth saying."
                      : `No articles in ${category} yet. Try another category.`
                  }
                />
              ) : (
                items.map((b, i) => (
                  <FadeUp key={b.id} delay={Math.min(i, 5) * 0.06}>
                    <Link to={b.path} className="group block" data-testid={`blog-${b.slug}`}>
                      <div className="relative aspect-[16/10] overflow-hidden bg-[#0c1728]">
                        {b.featuredImage && (
                          <img
                            src={b.featuredImage}
                            srcSet={srcSetFor(b.featuredImage)}
                            sizes="(min-width: 1024px) 30vw, 90vw"
                            alt={b.featuredImageAlt || b.title}
                            className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
                            loading="lazy"
                          />
                        )}
                      </div>
                      <div className="mt-6">
                        <div className="flex items-center flex-wrap gap-3 mb-4">
                          {b.category && <span className="overline">{b.category}</span>}
                          {b.publishedAt && (
                            <>
                              <span className="text-xs text-[#9ca3af]">·</span>
                              <span className="text-xs text-[#9ca3af]">{formatSGDate(b.publishedAt)}</span>
                            </>
                          )}
                          {b.readingTimeMinutes && (
                            <>
                              <span className="text-xs text-[#9ca3af]">·</span>
                              <span className="text-xs text-[#9ca3af]">
                                {b.readingTimeMinutes} min read
                              </span>
                            </>
                          )}
                        </div>
                        <h2 className="font-serif text-3xl lg:text-4xl text-[#f7f5f0] leading-tight group-hover:text-[#cca24d] transition-colors">
                          {b.title}
                        </h2>
                        {b.excerpt && (
                          <p className="mt-4 text-[#9ca3af] leading-relaxed max-w-lg">{b.excerpt}</p>
                        )}
                        <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#f7f5f0] group-hover:text-[#cca24d] transition-colors">
                          Read <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </div>
                      </div>
                    </Link>
                  </FadeUp>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <nav className="mt-16 flex items-center justify-center gap-3" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="h-11 px-5 border border-[#1a2940] text-sm text-[#d8d8d8]
                    hover:border-[#cca24d]/50 disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-[#9ca3af] tnum px-4">
                  {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-11 px-5 border border-[#1a2940] text-sm text-[#d8d8d8]
                    hover:border-[#cca24d]/50 disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        )}
      </section>
    </>
  );
}
